import { useCallback, useEffect, useState } from 'react'
import styles from './ClaimManagementPage.module.css'
import Navbar from '../../components/Navbar/Navbar'
import CustomButton from '../../components/UI/CustomButton/CustomButton'
import Select from 'react-select'
import CustomModal from '../../components/UI/CustomModal/CustomModal'
import TableFilters from '../../components/TableFilters/TableFilters'
import CreateClaim from '../../components/CreateClaim/CreateClaim'
import ClaimTable from '../../components/ClaimTable/ClaimTable'
import ClaimFilters from '../../components/ClaimFilters/ClaimFilters'
import { useMediaQuery } from 'react-responsive'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { getAllClaimsFromPlatforms } from '../../redux/actions/claimsAction'
import Loader from '../../components/UI/Loader/Loader'
import { establishmentsPerPage, monthsArr } from '../../constants/constants'
import plusIcon from '../../assets/icons/plus.svg'
import iconFiltersMobile from '../../assets/icons/iconFiltersMobile.svg'
import { useRequestStatus } from '../../hooks/useRequestStatus'
import { getPlaces } from '../../redux/actions/placesAction'
import { useDebounce } from '../../hooks/useDebounce'
import { getMe } from '../../redux/actions/meAction'
import { authLogout } from '../../redux/reducers/auth/authSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import PATH from '../../navigation/path'
import { EstablishmentsType } from '../../redux/actions/types'
import {
  getEstablishmentFromCompany,
  getEstablishments,
} from '../../redux/actions/establishmentsAction'
import { useToast } from '../../context/ToastContext'

const ClaimManagementPage = () => {
  const now = new Date()
  const month = now.getMonth()
  const year = now.getFullYear()

  const initialYearsAndMonth = `${year}-${String(month + 1).padStart(2, '0')}`

  const [search, setSearch] = useState<string>('')
  const [createClaimModal, setCreateClaimModal] = useState(false)
  const [filtersModal, setFiltersModal] = useState(false)
  const [yearsAndMonth, setYearsAndMonth] = useState(initialYearsAndMonth)
  const [monthNumber, setMonthNumber] = useState<number>(month)
  const [claimStatus, setClaimStatus] = useState<any>('')
  const [claimError, setClaimError] = useState<string>('')
  const [checkedState, setCheckedState] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedEstablishment, setSelectedEstablishment] = useState('')
  const { showError } = useToast()
  const debouncedStatus = useRequestStatus(claimStatus)
  const debounced = useDebounce(search)

  const { isLoading, allClaimsFromPlaces } = useAppSelector((state) => state.claims)
  const { places } = useAppSelector((state) => state.places)
  const { establishment } = useAppSelector((state) => state.establishments)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { token, userRole, user } = useAppSelector((state) => state.auth)

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  const dummyData = (array: EstablishmentsType[]) => {
    const items: any[] = array.map((establishment) => {
      return {
        id: establishment?.id,
        title: establishment?.name,
      }
    })

    return items
  }

  const location = useLocation()

  const handleSelectChange = (selectedOption: { value: string; label: string } | null) => {
    setSelectedEstablishment(selectedOption?.value || '')
  }

  useEffect(() => {
    if (location.state) {
      dispatch(
        getEstablishments({
          token,
          page: currentPage,
          itemsPerPage: establishmentsPerPage,
          city: location.state.city,
          size: location.state.guestsNumber,
          name: location.state.name,
          address: location.state.address,
        }),
      )
    } else {
      if (
        userRole === 'directorPlace' ||
        userRole === 'employeeAgent' ||
        userRole === 'employeePlace' ||
        userRole === 'directorAgent'
      ) {
        dispatch(
          getEstablishmentFromCompany({
            token,
            page: currentPage,
            itemsPerPage: establishmentsPerPage,
          }),
        )
      } else {
        dispatch(
          getEstablishments({ token, page: currentPage, itemsPerPage: establishmentsPerPage }),
        )
      }
    }
  }, [dispatch, currentPage, setCurrentPage, establishmentsPerPage])

  const handleButtonClick = useCallback(
    async (btnTitle: string) => {
      if (btnTitle === 'Все') {
        setClaimStatus('')
        setSelectedEstablishment('')

        return
      }
      if (btnTitle === 'Завершенные') {
        setClaimStatus('accepted')
        setSelectedEstablishment('')
        return
      }
      if (btnTitle === 'В работе') {
        setClaimStatus('inProgress')
        setSelectedEstablishment('')
        return
      }
      if (btnTitle === 'Отклонены') {
        setClaimStatus('rejected')
        return
      }
    },
    [debouncedStatus],
  )

  const onCheckFilters = async (checkedFilters: string[]) => {
    if (
      userRole === 'directorPlace' ||
      userRole === 'employeePlace' ||
      userRole === 'directorAgent'
    ) {
      const resultAction = await dispatch(
        getAllClaimsFromPlatforms({
          companyId: String(user?.employee?.company?.id),
          yearsAndMonth,
          token,
          status: debouncedStatus,
          places: checkedFilters.join(','),
          type: selectedEstablishment,
        }),
      )

      if (getAllClaimsFromPlatforms.rejected.match(resultAction)) {
        showError(' Произошла ошибка при выборе залов')
      } else {
        setClaimError('')
        setFiltersModal(false)
      }
    } else {
      const resultAction = await dispatch(
        getAllClaimsFromPlatforms({
          yearsAndMonth,
          token,
          status: debouncedStatus,
          places: checkedFilters.join(','),
          type: selectedEstablishment,
        }),
      )

      if (getAllClaimsFromPlatforms.rejected.match(resultAction)) {
        showError(' Произошла ошибка при выборе залов')
      } else {
        setClaimError('')
        setFiltersModal(false)
      }
    }
  }

  const { clients } = useAppSelector((state) => state.clients)

  const onCheckMonthHandler = async (month: string, year: string) => {
    const monthLength = String(monthsArr.indexOf(month) + 1).length
    const date =
      monthLength === 1
        ? `${year}-0${monthsArr.indexOf(month) + 1}`
        : `${year}-${monthsArr.indexOf(month) + 1}`
    setYearsAndMonth(date)

    if (
      userRole === 'directorPlace' ||
      userRole === 'employeePlace' ||
      userRole === 'directorAgent'
    ) {
      await dispatch(
        getAllClaimsFromPlatforms({
          companyId: String(user?.employee?.company?.id),
          yearsAndMonth: date,
          token,
          type: selectedEstablishment,
        }),
      )
    } else {
      await dispatch(
        getAllClaimsFromPlatforms({ yearsAndMonth: date, token, type: selectedEstablishment }),
      )
    }

    dispatch(
      getPlaces({
        token,
        page: 1,
        itemsPerPage: 100,
        type: selectedEstablishment,
      }),
    )
  }

  useEffect(() => {
    if (!token) {
      return
    }

    if (userRole !== 'superAdmin' && userRole !== 'admin') {
      dispatch(getMe({ token }))
    }
  }, [dispatch, token])

  useEffect(() => {
    if (
      userRole === 'directorPlace' ||
      userRole === 'employeePlace' ||
      userRole === 'directorAgent'
    ) {
      if (!user?.employee?.company?.id) {
        return
      }

      if (debounced) {
        dispatch(
          getAllClaimsFromPlatforms({
            companyId: String(user?.employee?.company?.id),
            yearsAndMonth,
            token,
            status: debouncedStatus,
            search: debounced,
          }),
        )
      } else {
        dispatch(
          getAllClaimsFromPlatforms({
            companyId: String(user?.employee?.company?.id),
            yearsAndMonth,
            token,
            status: debouncedStatus,
          }),
        )
      }
    } else {
      if (debounced) {
        dispatch(
          getAllClaimsFromPlatforms({
            yearsAndMonth,
            token,
            status: debouncedStatus,
            search: debounced,
          }),
        )
      } else {
        dispatch(getAllClaimsFromPlatforms({ yearsAndMonth, token, status: debouncedStatus }))
      }
    }
  }, [dispatch, yearsAndMonth, debouncedStatus, debounced, user?.employee?.company?.id])

  useEffect(() => {
    if (!token) {
      return
    }

    const fetchPlaces = async () => {
      const resultAction = await dispatch(
        getPlaces({
          token,
          page: 1,
          itemsPerPage: 100,
          type: selectedEstablishment,
        }),
      )

      if (getPlaces.rejected.match(resultAction)) {
        const errorMessage = resultAction.payload
        if (errorMessage === 'Unauthorized') {
          dispatch(authLogout())
          navigate(PATH.authPage)
          window.location.reload()
        }
      }
    }

    fetchPlaces()
  }, [dispatch, token, userRole, selectedEstablishment])

  return (
    <div className={styles.claimManagementPage}>
      <Navbar />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Заявки залов</h1>
        </div>
        <TableFilters
          filtersMobile={
            isMobile ? (
              <CustomButton
                title=''
                iconSrc={iconFiltersMobile}
                className={styles.filtersBtn}
                onClick={() => setFiltersModal(true)}
              />
            ) : (
              <></>
            )
          }
          customButton={
            userRole !== 'employeePlace' ? (
              <CustomButton
                iconSrc={isMobile ? plusIcon : ''}
                className={styles.addSiteBtn}
                title={!isMobile ? 'Создать заявку' : ' '}
                type='button'
                onClick={() => setCreateClaimModal(true)}
              />
            ) : (
              <></>
            )
          }
          isCompany
          firstBtnTitle='Все'
          secondBtnTitle='Завершенные'
          thirdBtnTitle='В работе'
          handleFirstButtonClick={handleButtonClick}
          handleSecondButtonClick={handleButtonClick}
          handleThirdButtonClick={handleButtonClick}
          handleFourthButtonClick={handleButtonClick}
          search={search}
          setSearch={setSearch}
          debouncedStatus={debouncedStatus}
        />

        <div className={styles.content}>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {filtersModal && (
                <ClaimFilters
                  mobileFilter={
                    <Select
                      name='establishmentName'
                      options={dummyData(establishment.establishments).map((establishment) => ({
                        value: establishment?.id,
                        label: establishment?.title,
                      }))}
                      onChange={handleSelectChange}
                      value={
                        dummyData(establishment.establishments).find(
                          (option) => option?.id === selectedEstablishment,
                        )
                          ? {
                              value: selectedEstablishment,
                              label:
                                dummyData(establishment.establishments).find(
                                  (option) => option?.id === selectedEstablishment,
                                )?.title || '',
                            }
                          : null
                      }
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: 'none',
                          boxShadow: 'none',
                          minHeight: 'auto',
                          backgroundColor: 'transparent',
                          height: 28,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 5,
                          right: 0,
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: 0,
                          padding: 0,
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                      }}
                      placeholder='Площадка'
                      className={styles.reactSelect}
                      classNamePrefix='select'
                    />
                  }
                  error={claimError}
                  checkedState={checkedState}
                  setCheckedState={setCheckedState}
                  onClickHandler={() => setFiltersModal(false)}
                  platforms={places.places}
                  callback={onCheckFilters}
                />
              )}
              <ClaimTable
                selectPlatfrom={
                  !isMobile ? (
                    <Select
                      name='establishmentName'
                      options={dummyData(establishment.establishments).map((establishment) => ({
                        value: establishment?.id,
                        label: establishment?.title,
                      }))}
                      onChange={handleSelectChange}
                      value={
                        dummyData(establishment.establishments).find(
                          (option) => option?.id === selectedEstablishment,
                        )
                          ? {
                              value: selectedEstablishment,
                              label:
                                dummyData(establishment.establishments).find(
                                  (option) => option?.id === selectedEstablishment,
                                )?.title || '',
                            }
                          : null
                      }
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: 'none',
                          boxShadow: 'none',
                          minHeight: 'auto',
                          backgroundColor: 'transparent',
                          height: 28,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 5,
                          right: 0,
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: 0,
                          padding: 0,
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                      }}
                      placeholder='Площадка'
                      className={styles.reactSelect}
                      classNamePrefix='select'
                    />
                  ) : (
                    <></>
                  )
                }
                places={places.places}
                yearsAndMonth={yearsAndMonth}
                monthNumber={monthNumber}
                setMonthNumber={setMonthNumber}
                onOpenClaimFilters={() => setFiltersModal(true)}
                claims={allClaimsFromPlaces}
                onCheckMonthHandler={onCheckMonthHandler}
                type={selectedEstablishment}
              />
            </>
          )}
        </div>
      </div>
      <CustomModal visible={createClaimModal} setVisible={setCreateClaimModal}>
        <CreateClaim
          client={clients}
          onClickHandler={() => setCreateClaimModal(false)}
          customer={user.fullName}
        />
      </CustomModal>
    </div>
  )
}

export default ClaimManagementPage
